import React, { useContext, useEffect, useMemo, useState } from "react"

import { Button, Heading, useTheme } from "@chakra-ui/core"

import Paginator from "~tailwindui/Table/Paginator"

import EventEmitter from "src/EventEmitter"
import { teacherClient as client } from "src/urql-client"

import { gql, useQuery } from "hooks/urql"
import useWebsocket from "hooks/useWebsocket"

import AutocompleteSelect from "components/Forms/AutocompleteSelect"
import Layer from "components/Pages/Layer"
import ErrorBoundary from "components/Pages/Teacher/ErrorBoundary"
import TeacherContext from "components/Pages/Teacher/TeacherContext"
import UrqlLoadingIndicator from "components/WithLoadingIndicator/urql"

import NewThreadModal from "./NewThreadModal"
import ThreadsList from "./ThreadList"

const MessageThreads = () => {
  const [modalOpen, setModalOpen] = useState(false)
  const theme: any = useTheme()
  const viewer = useContext(TeacherContext)

  const [page, setPage] = useState(1)
  const [userFilter, setUserFilter] = useState<
    undefined | Record<string, string>
  >()
  const [result, refreshQuery] = useQuery({
    query: threadsQuery,
    variables: { page, userIdFilter: userFilter?.value },
    client,
  })

  useEffect(() => {
    setPage(1)
  }, [userFilter])

  useEffect(() => {
    if (!modalOpen) refreshQuery({ requestPolicy: "network-only" })
  }, [modalOpen, refreshQuery])

  const channelProps = useMemo(() => {
    return { id: viewer.id }
  }, [viewer.id])

  useWebsocket({
    channelName: "MessageUserChannel",
    channelProps,
  })

  useEffect(() => {
    const handleEvent = () => {
      refreshQuery({ requestPolicy: "network-only" })
    }
    EventEmitter.subscribe("messageReceived", handleEvent)

    return () => {
      EventEmitter.unsubscribe("messageReceived", handleEvent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layer>
      <ErrorBoundary name="messageThreads">
        <Heading fontSize="3xl" height="16" maxW="50%" isTruncated>
          Messages
        </Heading>

        <Button
          {...theme.buttonVariants.cloudroom}
          onClick={() => setModalOpen(true)}
        >
          New Thread
        </Button>
        <NewThreadModal modalOpen={modalOpen} setModalOpen={setModalOpen} />

        <UrqlLoadingIndicator result={result}>
          {({ data: { viewer } }) => (
            <>
              {(userFilter ||
                viewer.messageThreads.data.length > 3 ||
                viewer.messageThreads.totalPages > 1) && (
                <div className="mt-6 space-y-4 sm:space-y-0">
                  <div className="mr-6 inline-block w-[250px]">
                    <AutocompleteSelect
                      api="/api/teachers/tutors/autocomplete_full_name"
                      onChange={selected => setUserFilter(selected)}
                      isClearable
                      placeholder="Filter Threads by User"
                      value={userFilter}
                      menuPortalTarget={document.body}
                    />
                  </div>
                </div>
              )}

              <ThreadsList
                threads={viewer.messageThreads.data}
                viewerId={viewer.id}
              />
              <Paginator
                currentPage={viewer.messageThreads.currentPage}
                totalPages={viewer.messageThreads.totalPages}
                changePage={setPage}
              />
            </>
          )}
        </UrqlLoadingIndicator>
      </ErrorBoundary>
    </Layer>
  )
}

const threadsQuery = gql`
  query ThreadsQuery($page: Int!, $userIdFilter: ID) {
    viewer {
      id
      messageThreads(page: $page, userIdFilter: $userIdFilter) {
        currentPage
        totalPages
        data {
          id
          title
          messagesCount
          users {
            id
            type
            messagingDisplayName
            avatar {
              thumbUrl
            }
          }
          lastMessage {
            id
            read
            content
            createdAt
            author {
              id
              type
              messagingDisplayName
              avatar {
                thumbUrl
              }
            }
          }
        }
      }
    }
  }
`

export default MessageThreads
